/* eslint-disable react/jsx-pascal-case */
import { GetStaticProps } from 'next';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import React from 'react';
import { useLocation } from 'react-use';

import CTA from '@/components/CTA';
import Layout from '@/components/Layout';
import Awards from '@/components/Math/Awards';
import Blog from '@/components/Math/Blog';
import CoursePackage from '@/components/Math/CoursePackage';
import GoodReputation from '@/components/Math/GoodReputation';
import International from '@/components/Math/International';
import Teachers from '@/components/Math/Teachers';
import VideoArea from '@/components/Math/VideoArea';
import Banner from '@/components/MathEnglishBanner';

import Potential from '../../components/Math/Potential';

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  const translations = await serverSideTranslations(locale, [
    'common',
    'home',
    'path',
    'form',
    'english',
    'math',
  ]);
  return {
    props: {
      ...translations,
    },
  };
};

const Math = () => {
  const { t } = useTranslation(['math', 'common']);
  const state = useLocation();

  return (
    <>
      <NextSeo
        openGraph={{
          url: state?.href,
          title: t('common:seo-数学课程-title'),
          description: t('common:seo-数学课程-description'),
          images: [
            {
              url: 'https://cdnwukong.com/images/public/og/offical-site-1.png',
              width: 300,
              height: 300,
              alt: t('common:seo-数学课程-title'),
            },
          ],
        }}
      />
      <Head>
        <title>{t('common:seo-数学课程-title')}</title>
        <meta
          name="description"
          content={t('common:seo-数学课程-description')}
        />
        <meta name="keywords" content={t('common:seo-数学课程-关键词')} />
        <meta
          data-n-head="ssr"
          data-hid="apple-mobile-web-app-title"
          name="apple-mobile-web-app-title"
          content={t('common:seo-数学课程-title')}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Course',
              name: t('common:seo-悟空数学'), //填写当前学科页面的(WuKong Chinese/WuKong Math/WuKong ELA Class), 对应中文为（悟空中文、悟空数学、悟空英语阅读写作）
              description: t('common:seo-数学课程-description'), //填写当前学科页面的meta description
              provider: {
                '@type': 'Organization',
                name: t('common:seo-悟空教育'), //中文为悟空教育
                url: state?.href, //首页url， 区分中文英文
              },
              educationalLevel: t('common:seo-数学-年龄'), //中文为3-18岁
              learningResourceType: t('common:seo-数学-资源类型'), //1v1在线中文直播课
              url: state?.href, //当前学科页url
              mainEntityOfPage: {
                '@type': 'WebPage',
                url: state?.href, //当前学科页url
              },
              potentialAction: {
                '@type': 'ReserveAction',
                name: t('common:seo-数学-约课名称'), //填写对应学科页的约课名称，见下表
                target: {
                  '@type': 'EntryPoint',
                  urlTemplate: t('common:seo-数学-约课链接'), //填写对应学科页的约课链接，见下表
                  actionPlatform: [
                    'https://schema.org/DesktopWebPlatform',
                    'https://schema.org/MobileWebPlatform',
                  ],
                },
                description: t('common:seo-数学-约课描述'), //填写对应学科页的约课描述，见下表
              },
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
              },
            }),
          }}
        />
      </Head>
      {/* Banner */}
      <Banner
        title="banner-标题"
        subTitle="banner-副标题"
        age="banner-年龄"
        btnName="banner-按钮"
        useTranslationName="math"
        subject="math"
        ga="O_MathCourse_FirstModule_CTAClick"
        ga4="new_entry_MathCourse_FirstModuleCTA"
        alt="seo-数学-student-banner"
      />

      {/* 奇妙殿堂 */}
      <VideoArea />

      {/* International */}
      <International />

      {/* 师资力量 Teachers */}
      <Teachers />

      {/* 奖项 */}
      <Awards />

      {/* 激发潜能 */}
      <Potential />

      {/* 口碑之选 */}
      <GoodReputation />

      {/* 课包 */}
      <CoursePackage />

      {/* Blog */}
      <Blog />

      {/* Line */}
      <section className="marginModelTop mb-20 lg:mb-[180px] xl:mb-[240px]">
        <CTA
          blockTitle="步骤之旅-标题"
          steps1name="步骤之旅-步骤-1"
          steps2name="步骤之旅-步骤-2"
          steps3name="步骤之旅-步骤-3"
          useTranslationName="math"
          subject="math"
        />
      </section>
    </>
  );
};

export default Math;

Math.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;
